import type { MapMarker } from '@gpx-spa/geo-domain';
import type {
  V2ShipmentDetail,
  V2ShipmentStop,
} from '@gpx-spa/queries/src/getAccountShipmentV2';
import { mapColorHex, mapFilterKey } from '@/utils/shipment-status-ui';

const LIST_MARKER_ID_RE = /^list-(\d+)-[OD]$/;

export function parseListMarkerShipmentId(markerId: string): number | null {
  const match = LIST_MARKER_ID_RE.exec(markerId);
  if (!match) return null;
  const id = Number(match[1]);
  return Number.isFinite(id) && id > 0 ? id : null;
}

export function buildShipmentsById(
  shipments: V2ShipmentDetail[],
): Map<number, V2ShipmentDetail> {
  return new Map(shipments.map((s) => [s.id, s]));
}

function isValidStopCenter(stop: V2ShipmentStop): boolean {
  const c = stop.center;
  return (
    c != null &&
    typeof c.lat === 'number' &&
    typeof c.lng === 'number' &&
    Number.isFinite(c.lat) &&
    Number.isFinite(c.lng)
  );
}

export function resolveOriginDestinationStops(stops: V2ShipmentStop[]): {
  origin: V2ShipmentStop | null;
  destination: V2ShipmentStop | null;
} {
  const sorted = [...stops].sort((a, b) => a.sequence - b.sequence);
  const withCenter = sorted.filter(isValidStopCenter);
  if (withCenter.length === 0) {
    return { origin: null, destination: null };
  }
  const origin = withCenter[0]!;
  const destination = withCenter[withCenter.length - 1]!;
  return { origin, destination };
}

function sameStopPosition(a: V2ShipmentStop, b: V2ShipmentStop): boolean {
  return a.center.lat === b.center.lat && a.center.lng === b.center.lng;
}

function buildEndpointMarker(
  shipment: V2ShipmentDetail,
  stop: V2ShipmentStop,
  label: 'O' | 'D',
  color: string,
  arcOrder: number,
): MapMarker {
  return {
    id: `list-${shipment.id}-${label}`,
    clusterGroupId: String(shipment.id),
    arc: {
      groupId: String(shipment.id),
      order: arcOrder,
      payload: { shipmentId: shipment.id },
    },
    position: { lat: stop.center.lat, lng: stop.center.lng },
    style: {
      kind: 'circle',
      fillColor: color,
      borderColor: '#ffffff',
      label,
      sizePx: 32,
      fontSizePx: 14,
      textColor: '#ffffff',
    },
    clusterColor: color,
  };
}

export function buildShipmentListMapPayload(
  shipments: V2ShipmentDetail[],
  options: { hiddenStatusKeys: Set<string> },
): {
  markers: MapMarker[];
  boundsCoords: [number, number][];
} {
  const markers: MapMarker[] = [];
  const boundsCoords: [number, number][] = [];

  for (const shipment of shipments) {
    const filterKey = mapFilterKey(shipment);
    if (options.hiddenStatusKeys.has(filterKey)) continue;

    const stops = shipment.stops ?? [];
    const { origin, destination } = resolveOriginDestinationStops(stops);
    if (!origin) continue;

    const color = mapColorHex(shipment);

    markers.push(buildEndpointMarker(shipment, origin, 'O', color, 0));
    boundsCoords.push([origin.center.lng, origin.center.lat]);

    const hasDistinctDestination =
      destination != null && !sameStopPosition(origin, destination);

    if (hasDistinctDestination && destination) {
      markers.push(buildEndpointMarker(shipment, destination, 'D', color, 1));
      boundsCoords.push([destination.center.lng, destination.center.lat]);
    }
  }

  return { markers, boundsCoords };
}
